import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import {
  AutomaticAcceptanceTimingConfiguration,
  automaticAcceptanceTimingConfigurationSchema,
} from '@procureai/schemas';
import { UseEditableWidgetBackend } from '@procureai/ui-modules-mui';

import {
  AutomaticAcceptanceTimingQuery,
  AutomaticAcceptanceTimingQueryVariables,
  OverrideAutomaticAcceptanceTimingMutation,
  OverrideAutomaticAcceptanceTimingMutationVariables,
  TsrctAutoAcceptTimingVariant,
  TsrctAutomaticAcceptanceTimingInput,
} from '@/generated/gql/graphql';
import { createEditableWidgetGraphqlBackendHook } from '@/shared/hooks/useEditableWidgetGraphqlBackend/useEditableWidgetGraphqlBackend';
import { isSourcingCaseEditable } from '~/common/guards/sourcingCase.guards';
import { ModuleEntity } from '~/common/types';
import { ModuleEntityType } from '~/common/types/ModuleEntity';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { isNegotiationCaseEditable } from '~/util/negotiationCase/isNegotiationCaseEditable';

import { OVERRIDE_AUTOMATIC_ACCEPTANCE_TIMING_MUTATION } from './AutomaticAcceptanceTimingWidget.mutation.gql.ts';
import { AUTOMATIC_ACCEPTANCE_TIMING_QUERY } from './AutomaticAcceptanceTimingWidget.query.gql.ts';

export type AutomaticAcceptanceTimingFormData = {
  automaticAcceptanceTimingConfiguration: AutomaticAcceptanceTimingConfiguration | null;
};

const isAutomaticAcceptanceTimingSupported = (entity: ModuleEntity) => {
  switch (entity) {
    case ModuleEntityType.NEGOTIATION_CASE:
    case ModuleEntityType.SOURCING_CASE:
      return true;
    default:
      return false;
  }
};

const getVariant = (entity: ModuleEntity): TsrctAutoAcceptTimingVariant =>
  entity === ModuleEntityType.SOURCING_CASE
    ? TsrctAutoAcceptTimingVariant.SOURCING_CASE
    : TsrctAutoAcceptTimingVariant.NEGOTIATION_CASE;

const isCaseEditable = (entity: ModuleEntity, data: AutomaticAcceptanceTimingQuery | null): boolean => {
  switch (entity) {
    case ModuleEntityType.NEGOTIATION_CASE:
      return isNegotiationCaseEditable(data?.tesseract.negotiationCase);
    case ModuleEntityType.SOURCING_CASE:
      return isSourcingCaseEditable(data?.tesseract.sourcingCase);
    default:
      return false;
  }
};

const useAutomaticAcceptanceTimingWidgetBackendInternal = createEditableWidgetGraphqlBackendHook<
  AutomaticAcceptanceTimingFormData,
  AutomaticAcceptanceTimingQuery,
  AutomaticAcceptanceTimingQueryVariables,
  OverrideAutomaticAcceptanceTimingMutation,
  OverrideAutomaticAcceptanceTimingMutationVariables
>({
  schema: automaticAcceptanceTimingConfigurationSchema
    .nullable()
    .transform((automaticAcceptanceTimingConfiguration) => ({ automaticAcceptanceTimingConfiguration })),
  query: AUTOMATIC_ACCEPTANCE_TIMING_QUERY,
  mutation: OVERRIDE_AUTOMATIC_ACCEPTANCE_TIMING_MUTATION,
});

export const useAutomaticAcceptanceTimingWidgetBackend: UseEditableWidgetBackend<AutomaticAcceptanceTimingFormData> = ({
  i18n,
}) => {
  const { entityId, entity } = useModuleContext();
  const { hasPermission } = usePermissions();
  const hasEditPermission = hasPermission(Permission.CaseUpdateNegotiationSettings);
  const variant = getVariant(entity);
  const supported = isAutomaticAcceptanceTimingSupported(entity);

  return useAutomaticAcceptanceTimingWidgetBackendInternal({
    i18n,
    variables: {
      targetId: entityId,
      variant,
      isNegotiationCase: entity === ModuleEntityType.NEGOTIATION_CASE,
      isSourcingCase: entity === ModuleEntityType.SOURCING_CASE,
    },
    skip: !entityId || !supported,
    toFormData: (data) => {
      const timing = data?.tesseract.automaticAcceptanceTiming?.timing;
      return {
        automaticAcceptanceTimingConfiguration: timing
          ? ({
              reactionMinMinutes: timing.reactionMinMinutes,
              reactionMaxMinutes: timing.reactionMaxMinutes,
              workingDays: timing.workingDays,
              workingHours: { start: timing.workingHours.start, end: timing.workingHours.end },
              timezone: timing.timezone,
              holiday: timing.holiday
                ? { country: timing.holiday.country, state: timing.holiday.state ?? undefined }
                : undefined,
            } as AutomaticAcceptanceTimingConfiguration)
          : null,
      };
    },
    toMutationData: (formData) => {
      const config = formData.automaticAcceptanceTimingConfiguration;
      const timing = {
        reactionMinMinutes: config?.reactionMinMinutes ?? 0,
        reactionMaxMinutes: config?.reactionMaxMinutes ?? 0,
        workingDays: config?.workingDays ?? [],
        workingHours: { start: config?.workingHours.start ?? '', end: config?.workingHours.end ?? '' },
        timezone: config?.timezone ?? '',
        holiday: config?.holiday ? { country: config.holiday.country, state: config.holiday.state ?? null } : null,
      } as TsrctAutomaticAcceptanceTimingInput;

      return { data: { targetId: entityId, variant, timing } };
    },
    toSimpleFormWidgetProps: (data, loading) => {
      return {
        disabled: loading || !supported || !hasEditPermission || !isCaseEditable(entity, data),
        loading,
      };
    },
  });
};
